import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronRight, ChevronDown, Lock, CheckCircle2, Circle, X, BookOpen, Home, FileText } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import API_BASE_URL from "../config";
import "./LessonSidebar.css";

let cachedModules = null;
let cachedExpandedModules = new Set();
let cachedSidebarScrollTop = 0;
let cachedAuthState = {
  initialized: false,
  isAuthenticated: false,
  substat: 0
};

export const LessonSidebar = ({ onLessonSelect }) => {
  const navigate = useNavigate();
  const { module: currentModule, lessonId: currentLessonIdFromRoute } = useParams();
  const [modules, setModules] = useState(() => cachedModules || []);
  const [loading, setLoading] = useState(() => !cachedModules);
  const [error, setError] = useState(null);
  const [expandedModules, setExpandedModules] = useState(() => new Set(cachedExpandedModules));
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => cachedAuthState.isAuthenticated);
  const [substat, setSubstat] = useState(() => cachedAuthState.substat);
  const desktopScrollRef = useRef(null);
  const mobileScrollRef = useRef(null);

  // Проверка авторизации и получение substat
  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        credentials: 'include' // Отправляем cookies
      });
      
      if (response.ok) {
        const data = await response.json();
        const nextAuthState = {
          initialized: true,
          isAuthenticated: data.authenticated || true,
          substat: data.substat || 0
        };

        cachedAuthState = nextAuthState;
        setIsAuthenticated(nextAuthState.isAuthenticated);
        setSubstat(nextAuthState.substat);
      } else {
        // Если не авторизован
        cachedAuthState = {
          initialized: true,
          isAuthenticated: false,
          substat: 0
        };
        setIsAuthenticated(false);
        setSubstat(0);
      }
    } catch (err) {
      console.error("Ошибка получения информации о пользователе:", err);
      cachedAuthState = {
        initialized: true,
        isAuthenticated: false,
        substat: 0
      };
      setIsAuthenticated(false);
      setSubstat(0);
    }
  };

  // Загрузка модулей с сервера
  const fetchModules = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/api/lessons/modules`, {
        credentials: 'include' // Отправляем cookies
      });
      
      if (!response.ok) {
        throw new Error("Ошибка загрузки модулей");
      }
      
      const data = await response.json();
      cachedModules = data.modules || [];
      setModules(cachedModules);
      setError(null);
    } catch (err) {
      console.error("Ошибка загрузки модулей:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!cachedModules) {
      fetchModules();
    }

    if (!cachedAuthState.initialized) {
      checkAuth();
    }
  }, []);

  // Слушаем событие открытия/закрытия мобильного меню из Header
  useEffect(() => {
    const handleToggle = () => setIsOpen((prev) => !prev);
    window.addEventListener("sidebarToggle", handleToggle);
    return () => window.removeEventListener("sidebarToggle", handleToggle);
  }, []);

  // Обновляем auth-состояние без повторной загрузки sidebar
  useEffect(() => {
    const handleAuthChange = () => {
      checkAuth();
    };

    window.addEventListener("authChange", handleAuthChange);
    return () => {
      window.removeEventListener("authChange", handleAuthChange);
    };
  }, []);

  // Отслеживание изменений в localStorage для обновления состояния авторизации
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "jwt_token") {
        checkAuth();
      }
    };

    // Слушаем изменения localStorage в других вкладках
    window.addEventListener("storage", handleStorageChange);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleLessonCompletionChanged = (event) => {
      const { lessonId, completed } = event.detail || {};
      if (!lessonId) return;

      setModules((prevModules) => {
        const nextModules = prevModules.map((module) => ({
          ...module,
          lessons: (module.lessons || []).map((lesson) =>
            String(lesson.id) === String(lessonId)
              ? { ...lesson, completed }
              : lesson
          )
        }));

        cachedModules = nextModules;
        return nextModules;
      });
    };

    window.addEventListener("lessonCompletionChanged", handleLessonCompletionChanged);
    return () => {
      window.removeEventListener("lessonCompletionChanged", handleLessonCompletionChanged);
    };
  }, []);

  const toggleModule = (moduleId) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      cachedExpandedModules = new Set(next);
      return next;
    });
  };

  const handleSidebarScroll = (event) => {
    cachedSidebarScrollTop = event.currentTarget.scrollTop;
  };

  useLayoutEffect(() => {
    const scrollContainers = [desktopScrollRef.current, mobileScrollRef.current];

    scrollContainers.forEach((container) => {
      if (!container) return;
      container.scrollTop = cachedSidebarScrollTop;
    });
  }, [modules, expandedModules, currentModule, currentLessonIdFromRoute, loading, isOpen]);

  // Применение логики блокировки уроков на основе авторизации и substat
  const applyAccessControl = (modulesData) => {
    if (!isAuthenticated) {
      // Если не зарегистрирован - только первые 3 урока Module 1 открыты
      return modulesData.map(module => {
        if (module.id === 'module_1') {
          // Для Module 1: первые 3 урока открыты
          return {
            ...module,
            lessons: module.lessons.map((lesson, index) => ({
              ...lesson,
              locked: index >= 3
            }))
          };
        }
        // Для остальных модулей - все закрыты
        return {
          ...module,
          lessons: module.lessons.map(lesson => ({
            ...lesson,
            locked: true
          }))
        };
      });
    } else {
      // Определяем, какие модули должны быть открыты на основе substat
      const getOpenModules = (substatValue) => {
        switch (substatValue) {
          case 0:
            // Первые 3 урока в Module 1 открыты
            return { module_1: { all: false, firstThree: true } };
          case 1:
            // Все уроки Module 1 открыты
            return { module_1: { all: true } };
          case 2:
            // Все уроки Module 2 открыты
            return { module_2: { all: true } };
          case 3:
            // Все уроки Module 3 открыты
            return { module_3: { all: true } };
          case 4:
            // Все уроки Module 4 открыты
            return { module_4: { all: true } };
          case 5:
            // Все уроки Module 5 открыты
            return { module_5: { all: true } };
          case 6:
            // Все уроки Module 1 и Module 2 открыты
            return { module_1: { all: true }, module_2: { all: true } };
          case 7:
            // Все уроки Module 1, Module 2, Module 3 открыты
            return { module_1: { all: true }, module_2: { all: true }, module_3: { all: true } };
          case 8:
            // Все уроки Module 1, Module 2, Module 3, Module 4 открыты
            return { module_1: { all: true }, module_2: { all: true }, module_3: { all: true }, module_4: { all: true } };
          case 9:
            // Все уроки Module 1, Module 2, Module 3, Module 4, Module 5 открыты
            return { module_1: { all: true }, module_2: { all: true }, module_3: { all: true }, module_4: { all: true }, module_5: { all: true } };
          default:
            // По умолчанию - первые 3 урока в Module 1 открыты
            return { module_1: { all: false, firstThree: true } };
        }
      };

      const openModules = getOpenModules(substat);
      if (substat >= 1) {
        openModules.module_6 = { all: true };
      }

      return modulesData.map(module => {
        const moduleAccess = openModules[module.id];
        
        if (!moduleAccess) {
          // Модуль не открыт - все уроки закрыты
          return {
            ...module,
            lessons: module.lessons.map(lesson => ({
              ...lesson,
              locked: true
            }))
          };
        }

        if (moduleAccess.all) {
          // Все уроки модуля открыты
          return {
            ...module,
            lessons: module.lessons.map(lesson => ({
              ...lesson,
              locked: false
            }))
          };
        }

        if (moduleAccess.firstThree) {
          // Только первые 3 урока открыты
          return {
            ...module,
            lessons: module.lessons.map((lesson, index) => ({
              ...lesson,
              locked: index >= 3
            }))
          };
        }

        // По умолчанию все закрыты
        return {
          ...module,
          lessons: module.lessons.map(lesson => ({
            ...lesson,
            locked: true
          }))
        };
      });
    }
  };

  const getLessonIcon = (lesson) => {
    if (lesson.locked) {
      return <Lock className="lesson-icon lesson-icon-locked" />;
    }
    if (lesson.completed) {
      return <CheckCircle2 className="lesson-icon lesson-icon-completed" />;
    }
    return <Circle className="lesson-icon lesson-icon-default" />;
  };

  // Применяем контроль доступа к модулям
  const modulesWithAccessControl = applyAccessControl(modules);

  const renderSidebarContent = (scrollRef) => (
    <div className="sidebar-container">
      <div className="sidebar-header">
        <h2 className="sidebar-title">Структура самоучителя</h2>
        <Button
          variant="ghost"
          size="icon"
          className="sidebar-close-btn"
          onClick={() => setIsOpen(false)}
        >
          <X className="sidebar-close-icon" />
        </Button>
      </div>
      
      <div
        ref={scrollRef}
        className="sidebar-content"
        onScroll={handleSidebarScroll}
      >
        {loading && (
          <div className="sidebar-loading">Загрузка...</div>
        )}
        
        {error && (
          <div className="sidebar-error">Ошибка: {error}</div>
        )}
        
        {!loading && !error && (
          <button
            onClick={() => {
              navigate("/");
              if (window.innerWidth < 768) setIsOpen(false);
            }}
            className={cn(
              "lesson-button lesson-button-prime",
              !currentModule && !currentLessonIdFromRoute && "lesson-button-active"
            )}
          >
            <Home className="lesson-icon lesson-icon-default" />
            <span className="lesson-title-menu">Что мы изучим?</span>
          </button>
        )}

        {!loading && !error && modulesWithAccessControl.map((module) => (
          <div key={module.id} className="module-item">
            {(() => {
              const totalLessons = module.lessons?.length || 0;
              const completedLessons = module.lessons?.filter(lesson => lesson.completed)?.length || 0;
              return (
            <button
              onClick={() => toggleModule(module.id)}
              className="module-button"
            >
              {expandedModules.has(module.id) ? (
                <ChevronDown className="module-chevron" />
              ) : (
                <ChevronRight className="module-chevron" />
              )}
              {`${module.title} (${completedLessons} / ${totalLessons})`}
            </button>
              );
            })()}
            
            {expandedModules.has(module.id) && (
              <div className="lessons-container">
                {module.id !== "module_6" && (
                <button
                  onClick={() => {
                    const moduleNumber = module.id.replace("module_", "");
                    navigate(`/${moduleNumber}/contents`);
                    if (window.innerWidth < 768) setIsOpen(false);
                  }}
                  className={cn(
                    "lesson-button",
                    currentModule === module.id.replace("module_", "") && currentLessonIdFromRoute === "contents" && "lesson-button-active"
                  )}
                >
                  <BookOpen className="lesson-icon lesson-icon-default" />
                  <span className="lesson-title-menu">Содержание модуля</span>
                </button>
                )}
                
                {(module.id === "module_5"
                  ? module.lessons || []
                  : [...(module.lessons || [])].sort((a, b) => {
                      const numA = parseFloat(String(a.lesson ?? "").replace(",", ".")) || 0;
                      const numB = parseFloat(String(b.lesson ?? "").replace(",", ".")) || 0;
                      if (numA !== numB) return numA - numB;
                      return (Number(a.id) || 0) - (Number(b.id) || 0);
                    })
                ).map((lesson, index) => {
                  const moduleNumber = module.id.replace("module_", "");
                  const isActive = currentModule === moduleNumber && currentLessonIdFromRoute === String(lesson.id);
                  
                  return (
                    <button
                      key={lesson.id}
                      onClick={() => {
                        if (lesson.locked) {
                          // Если пользователь не авторизован - редирект на авторизацию
                          if (!isAuthenticated) {
                            navigate("/auth");
                            if (window.innerWidth < 768) setIsOpen(false);
                            return;
                          }
                          // Если авторизован - показываем модальное окно с передачей номера модуля
                          onLessonSelect(lesson.id, lesson.locked, parseInt(moduleNumber));
                        } else {
                          navigate(`/${moduleNumber}/${lesson.id}`);
                        }
                        if (window.innerWidth < 768) setIsOpen(false);
                      }}
                      className={cn(
                        "lesson-button",
                        isActive && "lesson-button-active",
                        lesson.locked && "lesson-button-locked"
                      )}
                    >
                      {getLessonIcon(lesson)}
                      <span className="lesson-title-wrap">
                        {module.id === "module_6" ? (
                          <span className="lesson-title-menu">{lesson.title || `Статья ${index + 1}`}</span>
                        ) : (
                          <>
                            <span className="lesson-title-menu">Урок {index + 1}</span>
                            {lesson.title && (
                              <span className="lesson-subtitle-menu">{lesson.title}</span>
                            )}
                          </>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {!loading && !error && (
          <button
            onClick={() => {
              navigate("/contacts");
              if (window.innerWidth < 768) setIsOpen(false);
            }}
            className={cn(
              "lesson-button lesson-button-contact",
              (currentModule === "contacts" || currentLessonIdFromRoute === "contacts") && "lesson-button-active"
            )}
          >
            <FileText className="lesson-icon lesson-icon-default" />
            <span className="lesson-title-menu">Контакты</span>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      {/* Mobile Sidebar Overlay */}
      {isOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={cn("sidebar-mobile", isOpen && "sidebar-mobile-open")}>
        {renderSidebarContent(mobileScrollRef)}
      </aside>

      {/* Desktop Sidebar */}
      <aside className="sidebar-desktop">
        {renderSidebarContent(desktopScrollRef)}
      </aside>
    </>
  );
};

